import {ofType, StateObservable} from "redux-observable";
import { Observable, of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { switchMap, map, catchError } from 'rxjs/operators';
import {ViewIdentifiable} from "../../models/common/ViewSettings";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {ErrorAction} from "../../models/common/ActionTypes";
import {Help} from "../../models/common/Help";
import {createErrorResponse} from "../../config/languages/definitions/AjaxResponses";
import {AppState} from "../../reducers/RootReducer";
import {currentLanguageFromStore} from "../../config/LanguageConfig";

export type HelpActionTypes = HelpFetchAction | HelpFetchingErrors | HelpFetchingSuccess | HelpChangeViewableStateAction | ChangeCurrentHelpViewAction

export interface HelpFetchAction extends ViewIdentifiable {
    type: typeof HELP_FETCHING
}

export interface HelpFetchingSuccess extends ViewIdentifiable {
    type: typeof HELP_FETCHING_SUCCESS
    payload: Help
    languageIdentifier: string
}

export interface HelpFetchingErrors extends ViewIdentifiable, ErrorAction< typeof HELP_FETCHING_ERROR> {}

export const HELP_FETCHING = 'HELP_FETCHING';
export const HELP_FETCHING_SUCCESS = 'HELP_SUCCESS';
export const HELP_FETCHING_ERROR = "HELP_FAILURE";

export const fetchHelp = ( viewIdentifier: string): HelpFetchAction =>
    ( { type: HELP_FETCHING, viewIdentifier } );
export const fetchHelpSuccess = ( payload: Help, viewIdentifier: string, languageIdentifier: string ): HelpFetchingSuccess =>
    ({ type: HELP_FETCHING_SUCCESS, viewIdentifier, payload, languageIdentifier });
export const fetchHelpError = ( error: Error, viewIdentifier: string ): HelpFetchingErrors =>
    ({ type: HELP_FETCHING_ERROR,  viewIdentifier, error });

export const fetchHelpEpic = ( action$: Observable< HelpFetchAction >, store$: StateObservable< AppState >) =>
    action$.pipe(
        ofType( HELP_FETCHING ),
        switchMap( action => {
            const language = currentLanguageFromStore(store$);
            return ajax(APP_CONFIG.getRequestObject(`${APP_CONFIG.baseWeb}help/${action.viewIdentifier}/${ language.languageIdentifier }`)).pipe(
                        map(response => fetchHelpSuccess(
                            response.response as Help,
                            action.viewIdentifier,
                            language.languageIdentifier
                        )),
                        catchError(error => of(
                            fetchHelpError(error, action.viewIdentifier),
                            createErrorResponse(
                                language,
                                error.status,
                                "help",
                                "helpFetchAction")
                            )
                        )
                    )
                }
            )
        );

/**
 * open and close help view
 */
export interface HelpChangeViewableStateAction {
    type: typeof HELP_CHANGE_VIEWABLE_STATE
    isViewable: boolean
}

export const HELP_CHANGE_VIEWABLE_STATE = "HELP_CHANGE_VIEWABLE_STATE";

export const helpChangeViewableState = ( isViewable: boolean ): HelpChangeViewableStateAction => ({ isViewable, type: HELP_CHANGE_VIEWABLE_STATE });

/**
 * change current view identifier
 *
 * to keep track on which view the user currently is, this enables context based help,
 * when the user clicks on the help button in the main menu
 */
export interface ChangeHelpContextProp {
    changeCurrentHelpView: typeof changeCurrentHelpView
}
export interface ChangeCurrentHelpViewAction extends ViewIdentifiable {
    type: typeof CHANGE_CURRENT_HELP_VIEW
}

export const CHANGE_CURRENT_HELP_VIEW = "CHANGE_CURRENT_HELP_VIEW";

export const changeCurrentHelpView = ( viewIdentifier: string ): ChangeCurrentHelpViewAction =>
    ({ type: CHANGE_CURRENT_HELP_VIEW, viewIdentifier });

